import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../utils/config';

function AdditionalInfoModal({ isOpen, onClose, pswId, isAdminView = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    psw_id: pswId || '',
    photo: '',
    middle_name: '',
    city: '',
    province_or_state: '',
    postal_code: '',
    height: '',
    weight: '',
    reside_with_family_member: false,
    driver_license: false,
    mobility: '',
    working_permit: false,
    sin: '',
    marriage_status: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    availability: '',
    certifications: '',
    certification_expiry: '',
    insurance_coverage: '',
    languages_spoken: '',
    years_of_experience: '',
    specialty_areas: '',
    employment_history: '',
    employment_status_connection_healthcare: '',
    start_date: '',
    end_date: '',
    background_check_status: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen && pswId) {
      fetchAdditionalInfo();
    }
  }, [isOpen, pswId]);

  const fetchAdditionalInfo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let mergedData = { ...formData };
      
      // First, fetch user details from /admin/user/{pswId}
      if (pswId) {
        try {
          const userRes = await fetch(`${BACKEND_URL}/admin/user/${pswId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });

          if (userRes.ok) {
            const userDetailsData = await userRes.json();
            mergedData = { ...mergedData, ...(userDetailsData.user || userDetailsData) };
          }
        } catch (err) {
          console.error('Error fetching user details from /admin/user endpoint:', err);
        }
      }
      
      // Then, attempt to fetch fresh data from /admin/profile
      try {
        let profileResponse;
        
        if (isAdminView) {
          // Admin viewing user's additional info
          profileResponse = await fetch(`${BACKEND_URL}/admin/profile`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              id: pswId,
              user_type: 'psw'
            })
          });
        } else {
          // User viewing their own additional info
          profileResponse = await fetch(`${BACKEND_URL}/users/profile`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });
        }

        if (profileResponse.ok) {
          try {
            const profileData = await profileResponse.json();
            mergedData = { ...mergedData, ...(profileData.user || profileData) };
          } catch (jsonError) {
            console.error('Error parsing JSON:', jsonError);
          }
        }
      } catch (err) {
        console.error('Error fetching profile info:', err);
      }
      
      setFormData(mergedData);
    } catch (err) {
      console.error('Error fetching additional info:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Helper function to format date to ISO 8601 with UTC timezone
      const formatDateToISO = (dateStr) => {
        if (!dateStr) return null;
        return `${dateStr}T00:00:00Z`;
      };

      // Build payload with only fields matching the Rust struct and proper types
      const payload = {
        psw_id: formData.psw_id,
        photo: formData.photo || null,
        middle_name: formData.middle_name || null,
        city: formData.city || null,
        province_or_state: formData.province_or_state || null,
        postal_code: formData.postal_code || null,
        height: formData.height ? parseInt(formData.height, 10) : null,
        weight: formData.weight ? parseInt(formData.weight, 10) : null,
        reside_with_family_member: formData.reside_with_family_member || null,
        driver_license: formData.driver_license || null,
        mobility: formData.mobility || null,
        working_permit: formData.working_permit || null,
        sin: formData.sin || null,
        marriage_status: formData.marriage_status || null,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        availability: formData.availability || null,
        certifications: formData.certifications 
          ? formData.certifications.split(',').map(s => s.trim()).filter(s => s)
          : null,
        certification_expiry: formData.certification_expiry || null,
        insurance_coverage: formData.insurance_coverage || null,
        languages_spoken: formData.languages_spoken
          ? formData.languages_spoken.split(',').map(s => s.trim()).filter(s => s)
          : null,
        years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience, 10) : null,
        specialty_areas: formData.specialty_areas
          ? formData.specialty_areas.split(',').map(s => s.trim()).filter(s => s)
          : null,
        employment_history: formData.employment_history || null,
        employment_status_connection_healthcare: formData.employment_status_connection_healthcare || null,
        start_date: formatDateToISO(formData.start_date),
        end_date: formatDateToISO(formData.end_date),
        background_check_status: formData.background_check_status || null,
        notes: formData.notes || null,
      };

      const response = await fetch(`${BACKEND_URL}/users/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to save additional information');
      }

      try {
        await response.json();
        setSuccess('Additional information saved successfully!');
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (jsonError) {
        console.error('Error parsing JSON:', jsonError);
        throw new Error('Received an invalid response from the server.');
      }
    } catch (err) {
      setError(err.message || 'Failed to save information');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '900px', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Additional Information</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            ×
          </button>
        </div>

        {error && (
          <div style={{ color: '#ef4444', padding: '12px', background: '#fee', borderRadius: '4px', margin: '12px 20px' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ color: '#10b981', padding: '12px', background: '#d1fae5', borderRadius: '4px', margin: '12px 20px' }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="modal-form" style={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto', padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            
            {/* Personal Information Section */}
            <div style={{ gridColumn: '1 / -1' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Personal Information</h3>
            </div>

            <div className="form-group">
              <label htmlFor="middle_name">Middle Name</label>
              <input
                type="text"
                id="middle_name"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="marriage_status">Marital Status</label>
              <select
                id="marriage_status"
                name="marriage_status"
                value={formData.marriage_status}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">Select...</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>

            {/* Location Information Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Location Information</h3>
            </div>

            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="province_or_state">Province/State</label>
              <input
                type="text"
                id="province_or_state"
                name="province_or_state"
                value={formData.province_or_state}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="postal_code">Postal Code</label>
              <input
                type="text"
                id="postal_code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Physical Information Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Physical Information</h3>
            </div>

            <div className="form-group">
              <label htmlFor="height">Height (cm)</label>
              <input
                type="number"
                id="height"
                name="height"
                value={formData.height}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="weight">Weight (kg)</label>
              <input
                type="number"
                id="weight"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mobility">Mobility (ability to walk)</label>
              <input
                type="text"
                id="mobility"
                name="mobility"
                value={formData.mobility}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., Full mobility, Limited, Wheelchair"
              />
            </div>

            {/* Legal/Documentation Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Legal & Documentation</h3>
            </div>

            <div className="form-group">
              <label htmlFor="sin">SIN</label>
              <input
                type="text"
                id="sin"
                name="sin"
                value={formData.sin}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '28px' }}>
              <label style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  name="driver_license"
                  checked={formData.driver_license}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                Driver's License
              </label>
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '28px' }}>
              <label style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  name="working_permit"
                  checked={formData.working_permit}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                Working Permit
              </label>
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '28px' }}>
              <label style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  name="reside_with_family_member"
                  checked={formData.reside_with_family_member}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                Reside with Family Member
              </label>
            </div>

            {/* Emergency Contact Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Emergency Contact</h3>
            </div>

            <div className="form-group">
              <label htmlFor="emergency_contact_name">Emergency Contact Name</label>
              <input
                type="text"
                id="emergency_contact_name"
                name="emergency_contact_name"
                value={formData.emergency_contact_name}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="emergency_contact_phone">Emergency Contact Phone</label>
              <input
                type="tel"
                id="emergency_contact_phone"
                name="emergency_contact_phone"
                value={formData.emergency_contact_phone}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Professional Information Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Professional Information</h3>
            </div>

            <div className="form-group">
              <label htmlFor="years_of_experience">Years of Experience</label>
              <input
                type="number"
                id="years_of_experience"
                name="years_of_experience"
                value={formData.years_of_experience}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="languages_spoken">Languages Spoken</label>
              <input
                type="text"
                id="languages_spoken"
                name="languages_spoken"
                value={formData.languages_spoken}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., English, French, Spanish"
              />
            </div>

            <div className="form-group">
              <label htmlFor="certification_expiry">Certification Expiry</label>
              <input
                type="date"
                id="certification_expiry"
                name="certification_expiry"
                value={formData.certification_expiry}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="certifications">Certifications</label>
              <textarea
                id="certifications"
                name="certifications"
                value={formData.certifications}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
                placeholder="List your certifications..."
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="specialty_areas">Specialty Areas</label>
              <textarea
                id="specialty_areas"
                name="specialty_areas"
                value={formData.specialty_areas}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
                placeholder="e.g., Dementia care, Palliative care..."
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="availability">Availability</label>
              <textarea
                id="availability"
                name="availability"
                value={formData.availability}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
                placeholder="e.g., Monday-Friday, 9am-5pm..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="insurance_coverage">Insurance Coverage</label>
              <input
                type="text"
                id="insurance_coverage"
                name="insurance_coverage"
                value={formData.insurance_coverage}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Employment Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>Employment Details</h3>
            </div>

            <div className="form-group">
              <label htmlFor="employment_status_connection_healthcare">Employment Status (Connection Health Care)</label>
              <select
                id="employment_status_connection_healthcare"
                name="employment_status_connection_healthcare"
                value={formData.employment_status_connection_healthcare}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">Select...</option>
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Casual">Casual</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="start_date">Start Date</label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="end_date">End Date</label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="background_check_status">Background Check Status</label>
              <select
                id="background_check_status"
                name="background_check_status"
                value={formData.background_check_status}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">Select...</option>
                <option value="Pending">Pending</option>
                <option value="Completed">Completed</option>
                <option value="Expired">Expired</option>
              </select>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="employment_history">Employment History</label>
              <textarea
                id="employment_history"
                name="employment_history"
                value={formData.employment_history}
                onChange={handleChange}
                className="form-textarea"
                rows="4"
                placeholder="Previous employment details..."
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="form-textarea"
                rows="4"
                placeholder="Any additional notes..."
              />
            </div>
          </div>

          <div className="modal-actions" style={{ marginTop: '20px' }}>
            <button
              type="button"
              className="modal-button-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal-button-submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Information'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdditionalInfoModal;
